import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import {
  LayoutDashboard, Users, FileText, BarChart2,
  DollarSign, UserCog, LogOut, Menu, X, ShieldAlert,
  Ticket, FileText as QuoteIcon, TrendingUp, AlertCircle, Bot, History, Calendar as CalendarIcon, UsersRound, Wallet, RefreshCw, MessageSquare, UploadCloud, CalendarDays, Megaphone, Heart, MessageCircle, Stethoscope, FolderOpen, BadgeCheck, Mail, Home, Send, Handshake } from 'lucide-react';
import nebbLogo from '../../logo.png';
import TopBar from './TopBar';

// ── Fuente única de módulos habilitables para staff ────────────────────────
// REGLA: cada vez que se agrega un módulo al sistema, se agrega ACÁ (con su
// ruta, label, icono y, opcional, badge). Tanto el menú lateral como la
// pantalla de permisos de empleados (UsersPage) leen de esta misma lista, así
// nunca queda un módulo nuevo sin poder habilitarse a los empleados.
// `always: true` → siempre visible (no es un permiso togglable).
// Sin `mod` → no aparece como permiso (ej. Dashboard, Licencias).
export const STAFF_MODULES = [
  { mod: 'clients',  to: '/clients',  label: 'Clientes',      icon: Users },
  { mod: 'policies', to: '/policies', label: 'Pólizas',       icon: FileText },
  { mod: 'calendar', to: '/calendar', label: 'Calendario',    icon: CalendarIcon },
  { mod: 'claims',   to: '/claims',   label: 'Siniestros',    icon: ShieldAlert },
  { mod: 'renewals', to: '/renewals', label: 'Renovaciones',  icon: RefreshCw },
  { mod: 'chat',     to: '/chat',     label: 'Chat',          icon: MessageSquare, badgeKey: 'unreadChat' },
  { mod: 'tickets',  to: '/tickets',  label: 'Tickets',       icon: Ticket, badgeKey: 'myPendingTickets' },
  { mod: 'debts',    to: '/debts',    label: 'Deudores',      icon: AlertCircle, badgeKey: 'pendingDebts' },
  { mod: 'quotes',   to: '/quotes',   label: 'Cotizaciones',  icon: QuoteIcon, badgeKey: 'cotizacionesNuevas' },
  { mod: 'reports',  to: '/reports',  label: 'Reportes',      icon: BarChart2 },
  { mod: 'imports',  to: '/imports',  label: 'Cargas',        icon: UploadCloud },
];
// Lista plana {id,label} para los casilleros de permisos en UsersPage.
export const MODULOS = STAFF_MODULES.map(m => ({ id: m.mod, label: m.label }));

export default function Layout() {
  const { currentUser, logout, tickets, debts, quotes } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const myPendingTickets = (tickets || []).filter(t =>
    t.status !== 'done' && (t.assignedToId === currentUser?.id || t.assignedTo === currentUser?.name)
  ).length;

  const pendingDebts = (debts || []).filter(d => d.status === 'pending').length;

  // Solicitudes de licencia pendientes (solo admin): badge en el menú y
  // pop-up cuando entra una nueva. Consulta cada 60s.
  // Detectamos nuevas comparando los IDs ya vistos (no la cantidad: la lista
  // puede bajar al aprobar/rechazar, lo que rompía la comparación por número).
  const [pendingLic, setPendingLic] = useState(0);
  const seenLicRef = React.useRef(null); // Set de IDs vistos; null = primera carga

  // Mensajes de chat sin leer: consulta cada 12s + pop-up al llegar uno nuevo
  const { users, showToast } = useApp();

  React.useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    const check = async () => {
      try {
        const token = localStorage.getItem('nebb_token');
        const r = await fetch('/api/licenses/pending', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!r.ok) return;
        const lista = await r.json();
        if (!Array.isArray(lista)) return;
        const ids = new Set(lista.map(l => l.id));
        // En la primera carga solo registramos lo que hay, sin pop-up
        if (seenLicRef.current !== null) {
          const nuevas = lista.filter(l => !seenLicRef.current.has(l.id));
          if (nuevas.length > 0) {
            const ultima = nuevas[nuevas.length - 1];
            showToast(`📅 Nueva solicitud de licencia${ultima?.empName ? ` de ${ultima.empName}` : ''}`, 'warning');
          }
        }
        seenLicRef.current = ids;
        setPendingLic(lista.length);
      } catch (e) { /* silencioso */ }
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [currentUser, showToast]);
  const [unreadChat, setUnreadChat] = useState(0);
  const prevUnreadRef = React.useRef(null);
  React.useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch('/api/messages/summary', {
          headers: localStorage.getItem('nebb_token') ? { Authorization: `Bearer ${localStorage.getItem('nebb_token')}` } : {},
        });
        const d = await r.json();
        if (!Array.isArray(d)) return;
        setUnreadChat(d.reduce((s, x) => s + (x.unread || 0), 0));
        // Detectar mensajes nuevos respecto del chequeo anterior → pop-up
        const map = {};
        d.forEach(x => { map[x.userId] = x.unread || 0; });
        if (prevUnreadRef.current && location.pathname !== '/chat') {
          for (const [uid, n] of Object.entries(map)) {
            if (n > (prevUnreadRef.current[uid] || 0)) {
              const remitente = (users || []).find(u => u.id === uid);
              showToast && showToast(`💬 Nuevo mensaje de ${remitente?.name || 'un compañero'}`);
            }
          }
        }
        prevUnreadRef.current = map;
      } catch {}
    };
    check();
    const i = setInterval(check, 12000);
    return () => clearInterval(i);
  }, [users, showToast, location]);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Cotizaciones que hizo el bot y todavía nadie levantó. Al entrar a la
  // pantalla de Cotizaciones se marcan como vistas en el server y el
  // contador se apaga (localmente al toque, sin esperar a recargar).
  const [cotizacionesVistas, setCotizacionesVistas] = useState(false);
  const cotizacionesNuevas = cotizacionesVistas
    ? 0
    : (quotes || []).filter(q => q.nueva).length;

  React.useEffect(() => {
    if (location.pathname !== '/quotes' || cotizacionesVistas) return;
    if (!(quotes || []).some(q => q.nueva)) return;
    setCotizacionesVistas(true);
    const token = localStorage.getItem('nebb_token');
    fetch('/api/quotes/marcar-vistas', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    }).catch(() => {});
  }, [location.pathname, quotes, cotizacionesVistas]);

  // WhatsApp sin leer: la bandeja la sirve el backend, así que el contador
  // se consulta aparte (cada 15s). Si el endpoint no está, queda en 0 y el
  // menú no muestra nada — nunca rompe.
  const [waSinLeer, setWaSinLeer] = useState(0);
  React.useEffect(() => {
    let vivo = true;
    const check = () => {
      const token = localStorage.getItem('nebb_token');
      if (!token) return;
      fetch('/api/whatsapp/no-leidos', { headers: { Authorization: 'Bearer ' + token } })
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (vivo && j) setWaSinLeer(j.total || 0); })
        .catch(() => {});
    };
    check();
    const i = setInterval(check, 15000);
    return () => { vivo = false; clearInterval(i); };
  }, []);

  // Emisiones sin resolver: son las que están esperando la transferencia o
  // con el comprobante recibido sin emitir. Mismo criterio que WhatsApp —
  // la bandeja la sirve el backend, así que el contador se consulta aparte
  // y si el endpoint no está queda en 0: el menú nunca rompe.
  const [emisionesAbiertas, setEmisionesAbiertas] = useState(0);
  React.useEffect(() => {
    let vivo = true;
    const check = () => {
      const token = localStorage.getItem('nebb_token');
      if (!token) return;
      fetch('/api/emision/pendientes', { headers: { Authorization: 'Bearer ' + token } })
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (vivo && j) setEmisionesAbiertas(j.total || 0); })
        .catch(() => {});
    };
    check();
    const i = setInterval(check, 20000);
    return () => { vivo = false; clearInterval(i); };
  }, []);

  // Solicitudes de seguro de hogar de la web sin resolver (/hogar).
  const [hogarAbiertas, setHogarAbiertas] = useState(0);
  React.useEffect(() => {
    let vivo = true;
    const check = () => {
      const token = localStorage.getItem('nebb_token');
      if (!token) return;
      fetch('/api/hogar/pendientes', { headers: { Authorization: 'Bearer ' + token } })
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (vivo && j) setHogarAbiertas(j.total || 0); })
        .catch(() => {});
    };
    check();
    const i = setInterval(check, 30000);
    return () => { vivo = false; clearInterval(i); };
  }, []);

  // Bandeja del bot de correo: mails de las compañías esperando aprobación.
  const [correoPendientes, setCorreoPendientes] = useState(0);
  React.useEffect(() => {
    let vivo = true;
    const check = () => {
      const token = localStorage.getItem('nebb_token');
      if (!token) return;
      fetch('/api/mail-bot/pendientes', { headers: { Authorization: 'Bearer ' + token } })
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (vivo && j) setCorreoPendientes(j.pendientes || 0); })
        .catch(() => {});
    };
    check();
    const i = setInterval(check, 30000);
    return () => { vivo = false; clearInterval(i); };
  }, []);

  const badgeValues = { unreadChat, myPendingTickets, pendingDebts, cotizacionesNuevas };
  const NAV = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    ...STAFF_MODULES.map(m => ({
      to: m.to, label: m.label, icon: m.icon, mod: m.mod,
      badge: m.badgeKey ? badgeValues[m.badgeKey] : undefined,
    })),
    // Contactos y campañas de mail/WhatsApp (difusionModule.js). Solo admin.
    { to: '/difusion', label: 'Difusión', icon: Send, adminOnly: true },
    { to: '/licenses', label: 'Licencias', icon: CalendarDays, always: true, badge: pendingLic },
    // Solicitudes de emisión de asistencia en viaje. Va arriba de WhatsApp
    // a propósito: es la única pantalla donde hay plata de un cliente ya
    // transferida esperando que alguien haga algo.
    { to: '/emision', label: 'Emisión', icon: BadgeCheck, always: true, external: true, badge: emisionesAbiertas },
    // Lo que se vende en la web con SBI (hogar, monopatín, garantía de
    // alquiler): solicitudes para emitir y campañas (hogarModule.js).
    { to: '/hogar', label: 'Ventas web', icon: Home, always: true, external: true, badge: hogarAbiertas },
    // Correo de las compañías leído por el bot: siniestros, renovaciones,
    // pólizas y endosos propuestos para aprobar (mailBotModule.js).
    { to: '/correo', label: 'Correo', icon: Mail, always: true, external: true, badge: correoPendientes },
    // Bandeja de WhatsApp: también servida por el backend. Es donde el
    // equipo atiende los mensajes de los clientes (ver ARQUITECTURA.md §1).
    { to: '/whatsapp', label: 'WhatsApp', icon: MessageCircle, always: true, external: true, badge: waSinLeer },
    // Vida, Salud y Ahorro: pantalla propia servida por el backend (no es una
    // ruta de React), por eso va como enlace externo. Desde acá cualquiera
    // deriva un cliente al equipo de VSA.
    { to: '/vida', label: 'Vida y Ahorro', icon: Heart, always: true, external: true },
    // Biblioteca de coberturas y condiciones generales por ramo. La usa
    // el equipo y también el asistente para contestar y enviar al cliente.
    { to: '/documentos', label: 'Documentos', icon: FolderOpen, always: true, external: true },
  ].filter(item => {
    // Ítems solo-admin: visibles únicamente para el CEO/administrador.
    if (item.adminOnly) return currentUser?.role === 'admin';
    // Admin ve todo. El dashboard (sin mod) siempre visible.
    if (currentUser?.role === 'admin' || !item.mod || item.always) return true;
    // Staff: solo módulos habilitados (si no tiene lista, default clients+policies)
    const permitidos = currentUser?.modules || ['clients', 'policies'];
    return permitidos.includes(item.mod);
  });
  const NAV_ADMIN = [
    { to: '/ceo', label: 'CEO Dashboard', icon: TrendingUp },
    { to: '/bot', label: 'Asistente IA', icon: Bot },
    { to: '/insights', label: 'Inteligencia', icon: BarChart2 },
    { to: '/agent', label: 'Agente Marketing', icon: Megaphone },
    { to: '/employees', label: 'Empleados', icon: UsersRound },
    { to: '/finances', label: 'Finanzas', icon: Wallet },
    { to: '/commissions', label: 'Comisiones', icon: DollarSign },
    { to: '/activity', label: 'Actividad', icon: History },
    { to: '/users', label: 'Usuarios', icon: UserCog },
    // Aliados comerciales (automotoras del convenio): su URL con logo, su
    // usuario y la liquidación de comisiones (sociosModule.js).
    { to: '/socios', label: 'Aliados', icon: Handshake, external: true },
    // Por qué falló una cotización. Vivía en una dirección que había que
    // escribir a mano y nadie se acordaba: ahora está en el menú.
    { to: '/diagnostico', label: 'Diagnóstico', icon: Stethoscope, external: true },
  ];

  // OJO: esto es un elemento JSX, NO un componente (const X = () => ...).
  // Si fuera un componente definido dentro de Layout, React lo trataría como
  // un tipo nuevo en cada render y REMONTARÍA todo el sidebar (la <img> del
  // logo se vuelve a pintar => parpadeo con cada polling de tickets/chat).
  // Como elemento ya creado, React solo reconcilia y no remonta.
  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={nebbLogo} alt="" style={{ width: 36, height: 36, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 15, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>NEBB Brokers</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.04em' }}>Corredores de Seguros</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ padding: '16px 12px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 12px 10px', fontFamily: 'var(--font-display)', fontWeight: 600 }}>Menú</div>
        {NAV.map(({ to, label, icon: Icon, exact, badge, external }) => external ? (
          // Pantalla servida por el backend: enlace normal, no NavLink (React
          // Router lo interceptaría y terminaría en el catch-all del panel).
          <a key={to} href={to} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', borderRadius: 8, marginBottom: 2,
            textDecoration: 'none', fontSize: 13, fontWeight: 500,
            fontFamily: 'var(--font-display)', color: 'var(--text-secondary)',
            background: 'transparent', transition: 'all 0.15s',
          }}>
            <Icon size={16} />
            <span style={{ flex: 1 }}>{label}</span>
            {badge > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 700,
                minWidth: 18, height: 18, borderRadius: 9, display: 'flex',
                alignItems: 'center', justifyContent: 'center', padding: '0 5px',
              }}>{badge}</span>
            )}
          </a>
        ) : (
          <NavLink key={to} to={to} end={exact} style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', borderRadius: 8, marginBottom: 2,
            textDecoration: 'none', fontSize: 13, fontWeight: 500,
            fontFamily: 'var(--font-display)',
            color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
            background: isActive ? 'var(--accent-dim)' : 'transparent',
            transition: 'all 0.15s',
          })}>
            <Icon size={16} />
            <span style={{ flex: 1 }}>{label}</span>
            {badge > 0 && (
              <span style={{ background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>
                {badge}
              </span>
            )}
          </NavLink>
        ))}

        {currentUser?.role === 'admin' && (
          <>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '16px 12px 10px', fontFamily: 'var(--font-display)', fontWeight: 600 }}>Admin</div>
            {NAV_ADMIN.map(({ to, label, icon: Icon, external }) => external ? (
              // Pantalla servida por el backend: enlace normal, no NavLink
              // (React Router lo mandaría al catch-all del panel).
              <a key={to} href={to} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, marginBottom: 2,
                textDecoration: 'none', fontSize: 13, fontWeight: 500,
                fontFamily: 'var(--font-display)', color: 'var(--text-secondary)',
                background: 'transparent', transition: 'all 0.15s',
              }}>
                <Icon size={16} />
                {label}
              </a>
            ) : (
              <NavLink key={to} to={to} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, marginBottom: 2,
                textDecoration: 'none', fontSize: 13, fontWeight: 500,
                fontFamily: 'var(--font-display)',
                color: isActive ? 'var(--amber)' : 'var(--text-secondary)',
                background: isActive ? 'var(--amber-dim)' : 'transparent',
                transition: 'all 0.15s',
              })}>
                <Icon size={16} />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User */}
      <div style={{ padding: '16px 12px', borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, marginBottom: 6 }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: currentUser?.role === 'admin' ? 'var(--amber-dim)' : 'var(--accent-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: currentUser?.role === 'admin' ? 'var(--amber)' : 'var(--accent)',
            fontFamily: 'var(--font-display)',
          }}>
            {currentUser?.name?.charAt(0)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-display)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.name}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{currentUser?.role === 'admin' ? 'Administrador' : 'Administrativo'}</div>
          </div>
        </div>
        <button onClick={handleLogout} className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
          <LogOut size={13} /> Salir
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex' }}>
      {/* Desktop sidebar */}
      <aside className="sidebar" style={{ display: 'flex', flexDirection: 'column' }}>
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setOpen(false)} />
          <aside onClick={() => setOpen(false)} style={{ width: 240, background: 'var(--bg-secondary)', borderRight: '1px solid var(--border)', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)' }}>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="main-content" style={{ flex: 1 }}>
        {/* Mobile topbar */}
        <div className="mobile-topbar">
          <button onClick={() => setOpen(true)}><Menu size={20} /></button>
        </div>
        <Outlet />
      </main>
    </div>
  );
}